/* ============================================================
   The date header + week strip, the way Today and Plan open on mobile
   (CollapsibleCalendar + WeekStrip) — with room to spare, the strip is simply
   always there: the seven days of the week around the selected one, arrows to
   walk weeks, a "Today" chip when you're off today. Dots mark days with
   something scheduled. On the sky (`onSky`) the strip sits in a frosted pill
   and its ink follows the sky's.
   ============================================================ */
import React from 'react';
import { addDaysStr, toDayStr, todayStr, type WeekStart, weekStartOf } from '@habishine/client-core';

import { Icon } from './Icon';

function parseDay(day: string): Date {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function dayTitle(day: string): { eyebrow: string; title: string } {
  const date = parseDay(day);
  const eyebrow = date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase();
  const today = todayStr();
  const title = day === today ? 'Today' : day === addDaysStr(today, 1) ? 'Tomorrow' : day === addDaysStr(today, -1) ? 'Yesterday' : date.toLocaleDateString(undefined, { weekday: 'long' });
  return { eyebrow, title };
}

/* The time-of-day greeting the Today header opens with. One line, no name —
   the identity framing comes from the habits themselves. */
export function greeting(hour = new Date().getHours()): string {
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Winding down';
}

export function DayHeader({
  eyebrow, title, offToday, onToday, right, titleAccessory, onSky,
}: {
  eyebrow: string; title: React.ReactNode; offToday?: boolean; onToday?: () => void; right?: React.ReactNode; titleAccessory?: React.ReactNode; onSky?: boolean;
}) {
  const ink = onSky ? 'text-[var(--sky-ink)]' : 'text-ink';
  const eye = onSky ? 'text-[var(--sky-ink-2)]' : 'text-frog';
  return (
    <header className="mb-4 flex items-end gap-3">
      <div className="min-w-0 flex-1">
        <div className={`mb-1 text-[12px] font-bold uppercase tracking-[1.4px] ${eye}`}>{eyebrow}</div>
        <div className="flex items-center gap-3">
          {titleAccessory}
          <h1 className={`font-display truncate text-[38px] font-semibold leading-[1] md:text-[50px] ${ink}`}>{title}</h1>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 pb-1.5">
        {offToday && onToday ? (
          <button type="button" onClick={onToday} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold transition ${onSky ? 'glass text-[var(--sky-ink)] hover:brightness-105' : 'bg-card-sunk text-frog hover:brightness-95'}`}>
            <Icon name="corner-up-left" size={13} strokeWidth={2.4} /> Today
          </button>
        ) : null}
        {right}
      </div>
    </header>
  );
}

export function WeekStrip({
  selected, onSelect, dayCounts, weekStartsOn, onSky,
}: {
  selected: string; onSelect: (day: string) => void; dayCounts: Record<string, number>; weekStartsOn: WeekStart; onSky?: boolean;
}) {
  const today = todayStr();
  const start = toDayStr(weekStartOf(parseDay(selected), weekStartsOn));
  const days = Array.from({ length: 7 }, (_, i) => addDaysStr(start, i));
  const month = parseDay(selected).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const ink = onSky ? 'text-[var(--sky-ink)]' : 'text-ink';
  const soft = onSky ? 'text-[var(--sky-ink-2)]' : 'text-meta';
  const arrow = `rounded-full p-1.5 transition ${onSky ? 'text-[var(--sky-ink-2)] hover:bg-white/30 hover:text-[var(--sky-ink)]' : 'text-tertiary hover:bg-card-sunk hover:text-ink'}`;
  return (
    <div className="flex items-center gap-2">
      <button type="button" aria-label="Previous week" onClick={() => onSelect(addDaysStr(selected, -7))} className={`${arrow} hidden sm:block`}>
        <Icon name="chevron-left" size={18} strokeWidth={2.4} />
      </button>
      <div className={`grid min-w-0 flex-1 grid-cols-7 gap-0.5 md:gap-1 ${onSky ? 'glass rounded-row p-1 md:rounded-card md:p-1.5' : ''}`}>
        {days.map((day) => {
          const isToday = day === today;
          const isSel = !isToday && day === selected;
          const n = dayCounts[day] ?? 0;
          const past = day < today;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              aria-pressed={isSel || (isToday && selected === today)}
              aria-label={parseDay(day).toDateString()}
              className={`flex min-h-[54px] min-w-0 flex-col items-center justify-center gap-1 rounded-[14px] py-1.5 transition-[background-color,box-shadow,transform] duration-150 md:min-h-[58px] md:rounded-[16px] ${
                isToday
                  ? 'frog-gradient shadow-[0_8px_20px_-6px_rgba(122,63,208,0.6)]'
                  : isSel
                    ? 'bg-card shadow-[0_1px_2px_rgba(20,20,25,0.06),0_6px_16px_-8px_rgba(20,20,25,0.2)]'
                    : onSky ? 'hover:bg-white/35' : 'hover:bg-card-sunk'
              }`}
            >
              <span className={`text-[10.5px] font-bold uppercase tracking-[0.6px] ${isToday ? 'text-white/80' : isSel ? 'text-meta' : soft}`}>
                {parseDay(day).toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 3)}
              </span>
              <span className={`font-display num text-[19px] font-semibold leading-none ${isToday ? 'text-white' : isSel ? 'text-frog' : ink}`}>
                {parseDay(day).getDate()}
              </span>
              <span
                className="block rounded-full"
                style={
                  isToday ? { width: 5, height: 5, background: '#fff' }
                  : n > 0 ? { width: 5, height: 5, background: 'var(--success)' }
                  : past ? { width: 6, height: 6, border: '1.5px solid currentColor', opacity: 0.25 }
                  : { width: 5, height: 5, background: 'transparent' }
                }
              />
            </button>
          );
        })}
      </div>
      <button type="button" aria-label="Next week" onClick={() => onSelect(addDaysStr(selected, 7))} className={`${arrow} hidden sm:block`}>
        <Icon name="chevron-right" size={18} strokeWidth={2.4} />
      </button>
      {onSky ? null : <span className={`ml-1 hidden w-32 text-right text-[12px] font-semibold md:block ${soft}`}>{month}</span>}
    </div>
  );
}
